import { useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons/faMapMarkerAlt";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import ContactForm from "../ContactForm/ContactForm";
import { useInView } from "react-intersection-observer";
import "./Contact.css";
import { Helmet } from "react-helmet-async";

const Contact = () => {
  const location = useLocation();
  const isFromDownloadGuide = location.state?.isFromDownloadGuide || false;

  useEffect(() => {
    if (location.hash === "#get-in-touch") {
      const element = document.getElementById("get-in-touch");
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const pageTitle =
    "Contact - Apollo Financial Services | Financial Advisors Ireland";
  const pageDescription =
    "Get in touch with Apollo Financial Services for financial advice in Duleek, Co. Meath. Contact us via phone, email, or visit our office.";
  const canonicalUrl = "https://www.apollofinancial.ie/contact/";
  const pageKeywords =
    "pension advice Ireland, investment planning, tax planning services, estate planning advisor, mortgage advice Ireland, college savings plans, retirement income strategies, wealth preservation strategies, financial risk assessment, debt management advisor, financial planning for business owners, financial planning for expats in Ireland, retirement income planning, tax-efficient investment strategies, estate planning services, inheritance tax planning Ireland, succession planning for family businesses, mortgage advisory services, college fund planning Ireland, financial risk management, debt consolidation advice, wealth transfer strategies, charitable giving planning, life insurance planning, long-term care planning, investment portfolio management, retirement savings plans, employee benefits planning, business succession planning, financial goal setting services, tax planning for self-employed, financial planning for newlyweds, financial planning for retirees, financial planning for young professionals, financial planning for families, financial planning for divorcees, financial planning for widows, financial planning for high-net-worth individuals, financial planning for doctors, financial planning for lawyers, financial planning for teachers, financial planning for engineers, financial planning for IT professionals, financial planning for entrepreneurs, financial planning for freelancers, financial planning for consultants, financial planning for contractors, financial planning for artists, financial planning for musicians, financial planning for athletes.";


  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }), []);

  const itemVariants = useMemo(() => ({
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  }), []);

  const fadeInVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  }), []);

  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });


  const handleSuccess = useCallback(() => {
    console.log("Contact page form submitted successfully");
  }, []);

  const infoCards = useMemo(() => ([
    {
      title: "Email",
      icon: faEnvelope,
      content: (
        <p style={{ marginTop: "5px" }} aria-label="Email Apollo Financial Services">
          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "5px" }} />
          info@apollofinancial.ie
        </p>
      ),
      className: "info-card"
    },
    {
      title: "Phone",
      icon: faPhone,
      content: (
        <p style={{ marginTop: "5px" }} aria-label="Call Apollo Financial Services">
          <FontAwesomeIcon icon={faPhone} style={{ marginRight: "5px" }} />
          01 689 5066
        </p>
      ),
      className: "info-card gray"
    },
    {
      title: "Location",
      icon: faMapMarkerAlt,
      content: (
        <p style={{ marginTop: "5px" }}>
          <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: "5px" }} />
          Thomastown House, Thomastown, Duleek, Co. Meath, A92 X362
        </p>
      ),
      className: "info-card"
    }
  ]), []);

  return (
    <div className="contact-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.apollofinancial.ie/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Contact Us",
                item: "https://www.apollofinancial.ie/contact/",
              },
            ],
          })}
        </script>
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FinancialService",
          name: "Apollo Financial Services",
          image: "https://www.apollofinancial.ie/logo.png",
          "@id": "https://www.apollofinancial.ie",
          url: "https://www.apollofinancial.ie",
          telephone: "+353 1 689 5066",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Thomastown House, Thomastown",
            addressLocality: "Duleek",
            addressRegion: "Co. Meath",
            postalCode: " X362",
            addressCountry: "IE",
          },
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "09:00",
            closes: "17:00",
          },
          sameAs: [
            "https://www.facebook.com/ApolloFinancialServicesLtd",
            "https://www.linkedin.com/company/apollo-financial-services-limited/",
          ],
        })}
      </script>
      <motion.section
        ref={heroRef}
        className="contact-hero"
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={fadeInVariants}
      >
        <div className="hero-overlay">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
          >
            <motion.h1 variants={itemVariants}>Contact Us</motion.h1>
            <motion.p variants={itemVariants}>
              We're here to assist you. Get in touch for financial guidance
              tailored to your needs.
            </motion.p>
          </motion.div>
        </div>
      </motion.section>
      <section className="contact-details-form">
        <div className="contact-info">
          <h2>Our Contact Details</h2>
          <div className="info-grid">
            {infoCards.map((card) => (
              <div className={card.className} key={card.title}>
                <h3 style={{ marginBottom: "0px" }}>{card.title}</h3>
                {card.content}
              </div>
            ))}
          </div>
        </div>

        <div className="contact-form-section" id="get-in-touch">
          <h2>Get in Touch</h2>
          <div>
            <ContactForm
              onSuccess={handleSuccess}
              className="animated-contact-form"
              shouldOpenGuide={isFromDownloadGuide}
            />
          </div>
        </div>
      </section>
      <section className="contact-map">
        <h2>Find Us Here</h2>
        <div>
          <iframe
            title="Apollo Financial Services Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8165.913889628974!2d-6.482377334212612!3d53.65072467621904!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4867474ceb819991%3A0xd7cfcf1dc1171916!2sThomastown%2C%20Co.%20Meath%2C%20A92%20X362%2C%20Ireland!5e1!3m2!1sen!2suk!4v1753806048945!5m2!1sen!2suk"
            width="100%"
            height="400"
            style={{ border: "0" }}
            allowFullScreen
            loading="lazy"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;
